<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Full-width notice strip — launch dates, enrolment deadlines, promotions.
 *
 * Registered with `bleed: true`, so the renderer mounts this directly inside
 * the <section> and it supplies its own padding. It is deliberately NOT
 * position:sticky — the layer cannot know a consuming app's fixed-header
 * height, so a sticky strip would tuck under the app's navbar.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const dismissed = ref(false)

const isExternal = (url?: string | null) => !!url && /^(https?:|mailto:|tel:)/.test(url)

const toneClass = computed(() => ({
  accent: 'bg-[var(--site-accent)] text-[var(--site-accent-fg)]',
  surface: 'bg-[var(--site-surface)] border-y border-[var(--site-border)]',
  outline: 'border-y border-[var(--site-border)]'
}[props.data.tone ?? 'accent'] ?? 'bg-[var(--site-accent)] text-[var(--site-accent-fg)]'))
</script>

<template>
  <div
    v-if="!dismissed && data.text"
    class="w-full px-4 py-2.5 sm:px-6"
    :class="toneClass"
    role="region"
    aria-label="Announcement"
  >
    <div class="mx-auto flex max-w-5xl items-center justify-center gap-3 text-center text-sm">
      <p class="min-w-0">
        {{ data.text }}
        <NuxtLink
          v-if="data.link_url && data.link_text"
          :to="data.link_url"
          :external="isExternal(data.link_url)"
          class="ml-2 font-semibold underline underline-offset-2"
        >
          {{ data.link_text }}
        </NuxtLink>
      </p>
      <button
        v-if="data.dismissible"
        type="button"
        class="ml-auto shrink-0 opacity-70 transition-opacity hover:opacity-100"
        aria-label="Dismiss announcement"
        @click="dismissed = true"
      >
        <svg
          class="size-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path
            d="M18 6 6 18M6 6l12 12"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>
